// SkillPage — role-based onboarding guide, homepage "Get Started" visual style.
//
// Keeps the `── 0X // title ──` StepHeader rhythm, mono command lines, `/xapi`
// example rows and collapsible secondary paths from the original two-step page,
// but lays the flow out as a single left column of numbered steps:
//   1 install → 2 API key → 3 hand the key to the agent → 4 use (examples).
// A consumer / provider toggle swaps the copy, install command and examples.
// All copy comes from i18n (skill.c.* / skill.p.* / portal.getstarted.*).

import React from 'react';
import { XIcons } from '../primitives.jsx';
import { renderSkillRepoLine, XAPI_PROVIDER_REPO_URL, XAPI_SKILL_REPO_URL } from './market.jsx';
import { XShell } from '../shell.jsx';
import { useT } from '../i18n.jsx';

const CONSUMER_INSTALL_CMD = 'npx skills add xapi-labs/xapi-cli';
const PROVIDER_INSTALL_CMD = 'npx skills add xapi-labs/xapi-provider';
const CONSUMER_REPO = { url: XAPI_SKILL_REPO_URL, label: 'xapi-labs/xapi-cli' };
const PROVIDER_REPO = { url: XAPI_PROVIDER_REPO_URL, label: 'xapi-labs/xapi-provider' };

const useReveal = () => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('is-revealed'); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

// `── 0X // title ──` section divider — the shared homepage Get Started rhythm.
const StepHeader = ({ n, title, first }) => (
  <div className="row gap-2" style={{ alignItems: 'center', marginTop: first ? 0 : 30, color: 'var(--fg-4)' }}>
    <span style={{ flex: '0 0 auto' }}>──</span>
    <span style={{ color: 'var(--c-mint)', fontFamily: 'var(--font-mono)' }}>{n}</span>
    <span style={{ color: 'var(--fg-4)' }}>//</span>
    <span style={{ color: 'var(--fg-1)', fontWeight: 500, fontSize: 15 }}>{title}</span>
    <span style={{ flex: '1 1 auto', borderTop: '1px dashed var(--line-2)', marginLeft: 6 }} />
  </div>
);

const renderSlashCmd = (cmd) => {
  const m = /^(\/xapi\b)(.*)$/.exec(cmd || '');
  if (!m) return <span style={{ color: 'var(--fg-1)' }}>{cmd}</span>;
  return <><span className="gs-cmd-prefix">{m[1]}</span><span style={{ color: 'var(--fg-1)' }}>{m[2]}</span></>;
};

const CommandLine = ({ cmd, onCopy, copied }) => (
  <div className="gs-cmdline row between">
    <div className="gs-cmdline-text">
      <span className="term-prompt">$</span>
      <span style={{ color: 'var(--fg-1)' }}>{cmd}</span>
      <span className="gs-caret" aria-hidden="true">▍</span>
    </div>
    <button onClick={onCopy} className="btn btn-sm btn-ghost btn-icon" title="Copy" style={{ flexShrink: 0 }}>
      {copied ? <XIcons.IconCheck size={12} style={{ color: 'var(--ok)' }} /> : <XIcons.IconCopy size={12} />}
    </button>
  </div>
);

const ExampleRow = ({ cmd, idx = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`gs-ex-row reveal delay-${Math.min(idx + 1, 3)}`}>
      <div className="gs-ex-cmd mono">{renderSlashCmd(cmd)}</div>
    </div>
  );
};

const CollapsibleBlock = ({ n, title, badge, children }) => {
  const { t } = useT();
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderTop: '1px solid var(--line)' }}>
      <button
        type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="row between"
        style={{ width: '100%', padding: '18px 0', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', textAlign: 'left', alignItems: 'center', gap: 12 }}>
        <div className="row gap-3" style={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
          <span className="mono" style={{ color: 'var(--c-mint)', fontSize: 12, flexShrink: 0 }}>{n}</span>
          <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--fg-1)' }}>{title}</span>
          {badge && (
            <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', padding: '3px 7px', borderRadius: 3, background: 'var(--bg-2)', border: '1px solid var(--line-2)', color: 'var(--fg-3)', flexShrink: 0 }}>{badge}</span>
          )}
        </div>
        <span className="row gap-2" style={{ alignItems: 'center', color: 'var(--fg-3)', fontSize: 12, flexShrink: 0 }}>
          <span className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0 }}>{open ? t('portal.getstarted.collapse') : t('portal.getstarted.expand')}</span>
          <XIcons.IconChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </span>
      </button>
      {open && <div style={{ paddingBottom: 24 }}>{children}</div>}
    </div>
  );
};

// Step body + optional command / cta / scopes / examples, under a StepHeader.
const StepBody = ({ children }) => (
  <p style={{ margin: '12px 0 0', color: 'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 640 }}>{children}</p>
);

export const SkillPage = () => {
  const { t } = useT();
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const [copied, setCopied] = React.useState(null);
  const [role, setRole] = React.useState('consumer');
  const [scopesOpen, setScopesOpen] = React.useState(false); // 权限清单:折叠时只露前 2 条 + 渐变遮罩
  const copy = (text, id) => {
    try { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 1500); } catch {}
  };

  const isProvider = role === 'provider';

  const RoleTab = ({ value, label }) => (
    <button
      type="button" onClick={() => setRole(value)} aria-pressed={role === value}
      className="btn btn-sm"
      style={{
        border: '1px solid ' + (role === value ? 'var(--c-mint)' : 'var(--line-2)'),
        background: role === value ? 'var(--bg-2)' : 'transparent',
        color: role === value ? 'var(--fg-0)' : 'var(--fg-3)',
        fontWeight: role === value ? 600 : 400,
      }}
    >{label}</button>
  );

  const CtaButton = ({ label, onClick }) => (
    <button className="btn btn-sm btn-primary" style={{ marginTop: 14 }} onClick={onClick}>{label} <span className="arrow">→</span></button>
  );

  const RepoNote = ({ node }) => (
    <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--fg-4)', marginTop: 10 }}>{node}</div>
  );

  const SCOPE_KEYS = ['skill.p.s1.scope.create', 'skill.p.s1.scope.read', 'skill.p.s1.scope.update', 'skill.p.s1.scope.version', 'skill.p.s1.scope.publish'];
  const ScopeList = () => (
    <div style={{ marginTop: 14 }}>
      <div className="micro-mono" style={{ color: 'var(--fg-4)', marginBottom: 8 }}>{t('skill.p.s1.scopesLabel')}</div>
      {/* 折叠时只露前 2 条，底部渐变遮罩暗示还有更多；点开展开全部 */}
      <div style={{ position: 'relative' }}>
        <div className="col" style={{ gap: 7, maxHeight: scopesOpen ? 400 : 62, overflow: 'hidden', transition: 'max-height .28s ease' }}>
          {SCOPE_KEYS.map((k) => (
            <div key={k} className="row gap-2" style={{ alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--c-mint)', flexShrink: 0, marginTop: 1 }}><XIcons.IconCheck size={13} /></span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.5 }}>{t(k)}</span>
            </div>
          ))}
        </div>
        {!scopesOpen && (
          <div aria-hidden style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 40, background: 'linear-gradient(to bottom, transparent, var(--bg-0))', pointerEvents: 'none' }} />
        )}
      </div>
      <button
        type="button"
        onClick={() => setScopesOpen((o) => !o)}
        className="micro"
        style={{ marginTop: 8, background: 'transparent', border: 'none', color: 'var(--c-mint)', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        {scopesOpen ? t('skill.scopes.less') : t('skill.scopes.more')}
        <XIcons.IconChevronDown size={12} style={{ transform: scopesOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
    </div>
  );

  // Per-role content. 3 steps: 1 install · 2 API key (+ give it to the agent) · 3 use.
  // The "give the key to the agent" step is merged into the API-key step.
  const installCmd = isProvider ? PROVIDER_INSTALL_CMD : CONSUMER_INSTALL_CMD;
  const c = {
    install: { title: t('skill.c.s2.title'), body: t('skill.c.s2.body'), note: renderSkillRepoLine(t('skill.c.s2.note'), CONSUMER_REPO) },
    key: { title: t('skill.c.keystep.title'), body: t('skill.c.s1.body'), body2: t('skill.c.s3.body'), cta: t('skill.c.s1.cta') },
    use: { title: t('skill.c.s4.title'), body: t('skill.c.s4.body'), ex: ['skill.c.s4.ex1', 'skill.c.s4.ex2', 'skill.c.s4.ex3'] },
  };
  const p = {
    install: { title: t('skill.p.s2.title'), body: t('skill.p.s2.body'), note: renderSkillRepoLine(t('skill.p.s2.note'), PROVIDER_REPO) },
    key: { title: t('skill.p.keystep.title'), body: t('skill.p.s1.body'), body2: t('skill.p.s3.body'), cta: t('skill.p.s1.cta'), scopes: true },
    use: { title: t('skill.p.s4.title'), body: t('skill.p.s4.body'), ex: ['skill.p.s4.ex1', 'skill.p.s4.ex2', 'skill.p.s4.ex3'] },
  };
  const s = isProvider ? p : c;

  return (
    <div className="skill-shell" style={{ maxWidth: 1200, margin: '0 auto', padding: '28px clamp(16px, 4vw, 40px) 96px' }}>
      <section className="skill-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
        <div style={{ marginBottom: 30 }}>
          <div className="sec-num"><span className="grey">01</span> &nbsp;{t('portal.getstarted.eyebrow')}</div>
          <h2 className="gs-section-title" style={{ margin: '14px 0 0', fontSize: 'clamp(26px, 4vw, 42px)', letterSpacing: '-0.03em', fontWeight: 600, lineHeight: 1.08, textWrap: 'balance' }}>
            {isProvider ? t('skill.title.provider') : t('skill.title.consumer')}
          </h2>
          <div className="row gap-2" style={{ marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <RoleTab value="consumer" label={t('skill.role.consumer')} />
            <RoleTab value="provider" label={t('skill.role.provider')} />
            <span className="micro" style={{ color: 'var(--fg-4)', marginLeft: 4 }}>
              {isProvider ? t('skill.role.subProvider') : t('skill.role.subConsumer')}
            </span>
          </div>
        </div>

        {/* Left = setup (01 install, 02 API key + hand-off) · Right = 03 use.
            Same StepHeader rhythm as the original two-step page. Stacks ≤900px. */}
        <div className="getstarted-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 48, alignItems: 'start' }}>
          {/* Left column — setup steps */}
          <div>
            {/* 01 — Install */}
            <StepHeader n="01" title={s.install.title} first />
            <StepBody>{s.install.body}</StepBody>
            <div style={{ marginTop: 14 }}>
              <CommandLine cmd={installCmd} onCopy={() => copy(installCmd, 'inst')} copied={copied === 'inst'} />
              <RepoNote node={s.install.note} />
            </div>

            {/* 02 — API key (create + give it to the agent) */}
            <StepHeader n="02" title={s.key.title} />
            <StepBody>{s.key.body}</StepBody>
            <StepBody>{s.key.body2}</StepBody>
            {s.key.scopes && <ScopeList />}
            <CtaButton label={s.key.cta} onClick={() => navigate(authed ? 'console' : 'login')} />
          </div>

          {/* Right column — 03 use */}
          <div>
            <StepHeader n="03" title={s.use.title} first />
            <StepBody>{s.use.body}</StepBody>
            <div style={{ marginTop: 12 }}>
              {s.use.ex.map((k, i) => <ExampleRow key={role + k} idx={i} cmd={t(k)} />)}
            </div>
          </div>
        </div>

        {/* Secondary paths — install the CLI, stay updated */}
        <div style={{ borderBottom: '1px solid var(--line)', marginTop: 40 }}>
          <CollapsibleBlock n="04" title={t('portal.getstarted.cli.title')} badge={t('portal.getstarted.cli.badge')}>
            <p style={{ margin: '0 0 14px', color: 'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 720 }}>{t('portal.getstarted.cli.body')}</p>
            <CommandLine cmd={t('portal.getstarted.cli.cmd')} onCopy={() => copy(t('portal.getstarted.cli.cmd'), 'cli')} copied={copied === 'cli'} />
            <div className="micro-mono" style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--fg-4)', marginTop: 10 }}>{t('portal.getstarted.cli.note')}</div>
          </CollapsibleBlock>
          <CollapsibleBlock n="05" title={t('portal.getstarted.stay.title')}>
            <p style={{ margin: '0 0 14px', color: 'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 720 }}>{t('portal.getstarted.stay.body')}</p>
            <a href="https://x.com/xapi_to" target="_blank" rel="noreferrer noopener" className="btn btn-sm" style={{ textDecoration: 'none', display: 'inline-flex' }}>
              <XIcons.IconTwitter size={12} /> {t('portal.getstarted.stay.cta')} <XIcons.IconExternal size={11} />
            </a>
          </CollapsibleBlock>
        </div>
      </section>
    </div>
  );
};

export default SkillPage;
